import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';
import {
  DEFAULT_PRAYER_CONFIG,
  DEFAULT_FAJR_ANGLE,
  DEFAULT_DUHA_DURATION,
  DEFAULT_QIYAM_DURATION,
  type PrayerKey,
  type PrayerConfig,
} from '../utils/dayCycle';

/**
 * Location + prayer settings — the shared foundation for the light-based day
 * clock and the Islamic prayer-time layer.
 *
 * Persists with the established one-row-per-user JSONB pattern
 * (lifeBalanceStore / userStore): the whole { location, geoSource, prayer } blob
 * lives in `user_location_settings.data`, cloud-wins on login, fire-and-forget
 * upserts, localStorage cache for offline. Both features stay INACTIVE until a
 * location is set (per the brief — no fake default clock).
 */

export interface UserLocation {
  lat: number;
  lng: number;
  /** IANA zone id, e.g. "Asia/Riyadh" — stored (not an offset) for DST. */
  timeZone: string;
  /** Human label for display, e.g. "Riyadh, SA" or "My location". */
  label: string;
}

export type GeoSource = 'gps' | 'preset' | 'manual';

export interface PerPrayerTiming {
  /** Adhan → Iqamah gap (minutes). */
  waitMin: number;
  /** Congregational prayer length (minutes). */
  durationMin: number;
}

export interface PrayerSettings {
  enabled: boolean;
  ramadan: boolean;
  fajrAngle: number;
  perPrayer: Record<PrayerKey, PerPrayerTiming>;
  /**
   * The habit that prayer blocks complete — the canonical, user-controllable
   * link (by id, so renaming the habit is safe). Managed from Prayer Settings;
   * empty/dangling means "no habit linked yet" (the next link/use creates one).
   */
  habitId?: string;
  /** Optional voluntary prayers (off by default), with their block lengths. */
  duha: boolean;
  qiyam: boolean;
  duhaDurationMin: number;
  qiyamDurationMin: number;
  /** Per-voluntary-prayer linked habit ids (independent of the 5-prayer habit). */
  duhaHabitId?: string;
  qiyamHabitId?: string;
  /**
   * Optional manual start time (HH:MM) for a voluntary prayer. When set it
   * overrides the auto-derived time (Duha midway sunrise→noon; Qiyam last third
   * of the night). Cleared (undefined) = back to auto.
   */
  duhaTimeOverride?: string;
  qiyamTimeOverride?: string;
}

export const defaultPerPrayer = (): Record<PrayerKey, PerPrayerTiming> =>
  DEFAULT_PRAYER_CONFIG.reduce((acc, c) => {
    acc[c.key] = { waitMin: c.waitMin, durationMin: c.durationMin };
    return acc;
  }, {} as Record<PrayerKey, PerPrayerTiming>);

export const defaultPrayerSettings = (): PrayerSettings => ({
  enabled: false,
  ramadan: false,
  fajrAngle: DEFAULT_FAJR_ANGLE,
  perPrayer: defaultPerPrayer(),
  duha: false,
  qiyam: false,
  duhaDurationMin: DEFAULT_DUHA_DURATION,
  qiyamDurationMin: DEFAULT_QIYAM_DURATION,
});

/** Build the engine's PrayerConfig[] from the user's saved prayer settings. */
export function prayerConfigFromSettings(p: PrayerSettings): PrayerConfig[] {
  return DEFAULT_PRAYER_CONFIG.map((c) => ({
    key: c.key,
    name: c.name,
    waitMin: p.perPrayer[c.key]?.waitMin ?? c.waitMin,
    durationMin: p.perPrayer[c.key]?.durationMin ?? c.durationMin,
  }));
}

/** Parse an HH:MM string to a minute-of-day, or null when blank/invalid. */
function hhmmToMin(s: string | undefined): number | null {
  if (!s) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const min = Number(m[1]) * 60 + Number(m[2]);
  return Number.isFinite(min) && min >= 0 && min < 1440 ? min : null;
}

/** The voluntary start overrides (minute-of-day) to feed `computeDay`, when set. */
export function voluntaryOverridesFromSettings(p: PrayerSettings): { duhaStartMin?: number; qiyamStartMin?: number } {
  const out: { duhaStartMin?: number; qiyamStartMin?: number } = {};
  const duha = hhmmToMin(p.duhaTimeOverride);
  const qiyam = hhmmToMin(p.qiyamTimeOverride);
  if (duha != null) out.duhaStartMin = duha;
  if (qiyam != null) out.qiyamStartMin = qiyam;
  return out;
}

interface LocationState {
  location: UserLocation | null;
  geoSource: GeoSource | null;
  prayer: PrayerSettings;
  initializedUserId: string | null;
  cachedUserId: string | null;
  initialize: () => Promise<void>;
  setLocation: (location: UserLocation, source: GeoSource) => void;
  clearLocation: () => void;
  setPrayerEnabled: (enabled: boolean) => void;
  setRamadan: (ramadan: boolean) => void;
  setFajrAngle: (angle: number) => void;
  setPrayerTiming: (key: PrayerKey, patch: Partial<PerPrayerTiming>) => void;
  resetPrayerTimings: () => void;
  /** Set (or clear, with null) which habit the prayer blocks complete. */
  setPrayerHabitId: (habitId: string | null) => void;
  /** Toggle / tune the voluntary prayers (Duha, Qiyam) + their durations. */
  setPrayerVoluntary: (patch: Partial<Pick<PrayerSettings, 'duha' | 'qiyam' | 'duhaDurationMin' | 'qiyamDurationMin'>>) => void;
  /** Set (or clear) a voluntary prayer's linked habit. */
  setVoluntaryHabitId: (key: 'duha' | 'qiyam', habitId: string | null) => void;
  /** Set a voluntary prayer's manual start time (HH:MM), or null to reset to auto. */
  setVoluntaryTimeOverride: (key: 'duha' | 'qiyam', hhmm: string | null) => void;
}

const TABLE = 'user_location_settings';

function pickData(s: LocationState) {
  return { location: s.location, geoSource: s.geoSource, prayer: s.prayer };
}

/** Merge a possibly-partial stored prayer blob onto current defaults. */
function hydratePrayer(raw: any): PrayerSettings {
  const base = defaultPrayerSettings();
  if (!raw || typeof raw !== 'object') return base;
  const perPrayer = base.perPrayer;
  if (raw.perPrayer && typeof raw.perPrayer === 'object') {
    (Object.keys(perPrayer) as PrayerKey[]).forEach((k) => {
      const p = raw.perPrayer[k];
      if (p && typeof p === 'object') {
        perPrayer[k] = {
          waitMin: Number.isFinite(p.waitMin) ? p.waitMin : perPrayer[k].waitMin,
          durationMin: Number.isFinite(p.durationMin) ? p.durationMin : perPrayer[k].durationMin,
        };
      }
    });
  }
  return {
    enabled: !!raw.enabled,
    ramadan: !!raw.ramadan,
    fajrAngle: Number.isFinite(raw.fajrAngle) ? raw.fajrAngle : base.fajrAngle,
    perPrayer,
    habitId: typeof raw.habitId === 'string' ? raw.habitId : undefined,
    duha: !!raw.duha,
    qiyam: !!raw.qiyam,
    duhaDurationMin: Number.isFinite(raw.duhaDurationMin) ? raw.duhaDurationMin : base.duhaDurationMin,
    qiyamDurationMin: Number.isFinite(raw.qiyamDurationMin) ? raw.qiyamDurationMin : base.qiyamDurationMin,
    duhaHabitId: typeof raw.duhaHabitId === 'string' ? raw.duhaHabitId : undefined,
    qiyamHabitId: typeof raw.qiyamHabitId === 'string' ? raw.qiyamHabitId : undefined,
    duhaTimeOverride: typeof raw.duhaTimeOverride === 'string' ? raw.duhaTimeOverride : undefined,
    qiyamTimeOverride: typeof raw.qiyamTimeOverride === 'string' ? raw.qiyamTimeOverride : undefined,
  };
}

async function cloudUpsert(state: LocationState): Promise<void> {
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return;
    const { error } = await supabase.from(TABLE).upsert(
      { user_id: user.id, data: pickData(state), updated_at: new Date().toISOString() },
      { onConflict: 'user_id' }
    );
    if (error) console.error('Failed to sync location settings to Supabase:', error);
  } catch (error) {
    console.error('Error syncing location settings to Supabase:', error);
  }
}

export const useLocationStore = create<LocationState>()(
  persist<LocationState, [], [], any>(
    (set, get) => {
      const sync = () => void cloudUpsert(get());

      return {
        location: null,
        geoSource: null,
        prayer: defaultPrayerSettings(),
        initializedUserId: null,
        cachedUserId: null,

        initialize: () => dedupeInit('user_location_settings', async () => {
          const at = accountGeneration();
          try {
            const { user } = await syncManager.safeAuthCheck();
            if (!user) return;
            if (get().initializedUserId === user.id) return;
            // Cache-first boot: while this user's local cache is fresh, skip the
            // cloud read entirely (write-through keeps cloud current meanwhile).
            if (get().cachedUserId === user.id && isCloudFresh('user_location_settings', user.id)) {
              set({ initializedUserId: user.id });
              return;
            }

            const { data, error } = await supabase.from(TABLE).select('*');
            // An answer for an account that has since signed out is not this copy's.
            if (at !== accountGeneration()) return;
            if (error) throw error;

            const row = (data ?? [])[0];
            set((state) => {
              if (row && row.data && typeof row.data === 'object') {
                return {
                  location: row.data.location ?? null,
                  geoSource: row.data.geoSource ?? null,
                  prayer: hydratePrayer(row.data.prayer),
                  initializedUserId: user.id,
                  cachedUserId: user.id,
                };
              }
              const cacheIsOurs = state.cachedUserId === null || state.cachedUserId === user.id;
              return {
                ...(cacheIsOurs ? {} : { location: null, geoSource: null, prayer: defaultPrayerSettings() }),
                initializedUserId: user.id,
                cachedUserId: user.id,
              };
            });
            markCloudFresh('user_location_settings', user.id);
          } catch (error) {
            console.error('Failed to initialize location settings from Supabase:', error);
          }
        }),

        setLocation: (location, source) => {
          set({ location, geoSource: source });
          sync();
        },

        clearLocation: () => {
          set({ location: null, geoSource: null });
          sync();
        },

        setPrayerEnabled: (enabled) => {
          set((s) => ({ prayer: { ...s.prayer, enabled } }));
          sync();
        },

        setRamadan: (ramadan) => {
          set((s) => ({ prayer: { ...s.prayer, ramadan } }));
          sync();
        },

        setFajrAngle: (fajrAngle) => {
          set((s) => ({ prayer: { ...s.prayer, fajrAngle } }));
          sync();
        },

        setPrayerTiming: (key, patch) => {
          set((s) => ({
            prayer: {
              ...s.prayer,
              perPrayer: { ...s.prayer.perPrayer, [key]: { ...s.prayer.perPrayer[key], ...patch } },
            },
          }));
          sync();
        },

        resetPrayerTimings: () => {
          set((s) => ({ prayer: { ...s.prayer, fajrAngle: DEFAULT_FAJR_ANGLE, perPrayer: defaultPerPrayer() } }));
          sync();
        },

        setPrayerHabitId: (habitId) => {
          set((s) => ({ prayer: { ...s.prayer, habitId: habitId ?? undefined } }));
          sync();
        },

        setPrayerVoluntary: (patch) => {
          set((s) => ({ prayer: { ...s.prayer, ...patch } }));
          sync();
        },

        setVoluntaryHabitId: (key, habitId) => {
          const field = key === 'duha' ? 'duhaHabitId' : 'qiyamHabitId';
          set((s) => ({ prayer: { ...s.prayer, [field]: habitId ?? undefined } }));
          sync();
        },

        setVoluntaryTimeOverride: (key, hhmm) => {
          const field = key === 'duha' ? 'duhaTimeOverride' : 'qiyamTimeOverride';
          set((s) => ({ prayer: { ...s.prayer, [field]: hhmm ?? undefined } }));
          sync();
        },
      };
    },
    {
      name: 'location-settings-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 1,
      partialize: (state) => ({
        location: state.location,
        geoSource: state.geoSource,
        prayer: state.prayer,
        cachedUserId: state.cachedUserId,
      } as any),
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: the next account was shown the
   last one's location (where they live) and prayer settings until its own load, and its first edit
   before that load saved them — the whole blob is written — into its own row; an edit whose write
   never landed was replaced by the next account's load; and the next account's edit made before its
   own load was kept by the last one's load when its row had none. */
scopeToAccount(useLocationStore);
